import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { api_labelflow_training } from "../../../../config.jsx";

/**
 * Hook for Model Training Result page: inference, export, metrics.
 */
export default function useModelTrainingResult(modelType, versionId) {
  const [metricRows, setMetricRows] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [exportingOnnx, setExportingOnnx] = useState(false);
  const [exportingOpenVino, setExportingOpenVino] = useState(false);

  const baseQuery = useCallback(() => {
    if (!versionId || !modelType) return "";
    return `modelType=${encodeURIComponent(modelType)}&modelDataInfoId=${encodeURIComponent(String(versionId))}`;
  }, [modelType, versionId]);

  const fetchMetrics = useCallback(async () => {
    if (!versionId || !modelType) return;
    setMetricsLoading(true);
    try {
      const url = `${api_labelflow_training}/training_management/training_metrics?${baseQuery()}`;
      const resp = await axios.get(url);
      if (resp?.data?.rows) setMetricRows(resp.data.rows);
    } catch (err) {
      console.warn("Failed to load training metrics:", err);
    } finally {
      setMetricsLoading(false);
    }
  }, [modelType, versionId, baseQuery]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const runInference = useCallback(
    async (file, confidenceThreshold) => {
      if (!file || !versionId || !modelType) return null;
      const formData = new FormData();
      formData.append("file", file);
      const url = `${api_labelflow_training}/training_management/inference?${baseQuery()}&conf=${encodeURIComponent(confidenceThreshold)}`;
      const resp = await axios.post(url, formData, { responseType: "blob" });
      return resp.data;
    },
    [baseQuery, modelType, versionId]
  );

  const runInferenceJson = useCallback(
    async (file, confidenceThreshold) => {
      if (!file || !versionId || !modelType) return null;
      const formData = new FormData();
      formData.append("file", file);
      const url = `${api_labelflow_training}/training_management/inference_json?${baseQuery()}&conf=${encodeURIComponent(confidenceThreshold)}`;
      const resp = await axios.post(url, formData, { responseType: "json" });
      return resp?.data?.predictions || [];
    },
    [baseQuery, modelType, versionId]
  );

  const exportModel = useCallback(
    async (format) => {
      const url = `${api_labelflow_training}/training_management/export_model?${baseQuery()}&format=${encodeURIComponent(format)}`;
      const resp = await axios.get(url, { responseType: "blob" });
      return resp.data;
    },
    [baseQuery]
  );

  return {
    metricRows,
    metricsLoading,
    exportingOnnx,
    exportingOpenVino,
    setExportingOnnx,
    setExportingOpenVino,
    runInference,
    runInferenceJson,
    exportModel,
    fetchMetrics,
  };
}
